/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  action10002,
  action10061,
  crabDevice,
  queryStringifyUrl,
  replace as crabReplace,
  queryStringifyAction,
  navigate as xfnNavigate,
} from '@hfzq/crab-utils';
import { Action10061Options } from '@hfzq/crab-utils/esm/utils/navigation/action10061';
import { QueryStringParams } from '@hfzq/crab-utils/esm/utils/url/query-stringify';
import { saveMapMesgCross } from '@src/helper/common/map-mesg-cross-operate';
import { RouterConfig } from './history';

/**
 * 页面跳转方法， 使用压栈方式 跳转
 *
 * @param url 跳转地址
 * @param webviewConfig 小福牛webview配置项，在浏览器环境失效
 */
export function navigate(url: string, webviewConfig?: Action10061Options) {
  if (crabDevice.isTZT && !webviewConfig?.navigateInCurrent) {
    action10061(generateXFNUrl(url), { tzthiddentitle: 1, tztadjustnever: 1, ...webviewConfig });
    return;
  }
  RouterConfig.history.push(url, null);
}

/**
 * 页面跳转方法， 使用replace方式 跳转
 *
 * @param path 跳转地址
 * @param webviewConfig 小福牛webview配置项，在浏览器环境失效
 */
export function replace(url: string, webviewConfig?: Action10061Options) {
  if (crabDevice.isTZT && !webviewConfig?.navigateInCurrent) {
    xfnReplace(generateXFNUrl(url), { tzthiddentitle: 1, tztadjustnever: 1, ...webviewConfig });
    return;
  }
  RouterConfig.history.replace(url, null);
}

/**
 * app 将使用横屏来展示 在浏览器环境失效
 *
 * @param url 跳转地址
 * @param webviewConfig 小福牛webview配置项, 在浏览器环境失效
 */
export function fullscreen(url: string, webviewConfig?: Action10061Options) {
  if (crabDevice.isTZT && !webviewConfig?.navigateInCurrent) {
    xfnNavigate(
      queryStringifyAction('http://action:58210', {
        url: generateXFNUrl(url),
        tzthiddentitle: 1,
        tztadjustnever: 1,
        ...webviewConfig,
      })
    );
  }
}

export type ExtraQuery = QueryStringParams;
export type ExtraMapMesg = Record<string, unknown>;

export interface ExtraNavigateOptions<Q extends ExtraQuery, M extends ExtraMapMesg> {
  webviewConfig?: Action10061Options;
  query: Q;
  mapMesg: M;
}

/**
 * 跳转外部地址
 */
export async function extraNavigate<Q extends ExtraQuery, M extends ExtraMapMesg>(
  url: string,
  options?: ExtraNavigateOptions<Q, M>
) {
  await saveExtraMapMesg(options?.mapMesg);
  const fullUrl = queryStringifyUrl(generateExtraFullUrl(url), options?.query);
  if (crabDevice.isTZT) {
    action10061(fullUrl, options?.webviewConfig);
    return;
  }
  window.location.href = fullUrl;
}

/**
 * 使用replace方式跳转外部地址
 */
export async function extraReplace<Q extends ExtraQuery, M extends ExtraMapMesg>(
  url: string,
  options?: ExtraNavigateOptions<Q, M>
) {
  await saveExtraMapMesg(options?.mapMesg);
  const fullUrl = queryStringifyUrl(generateExtraFullUrl(url), options?.query);
  if (crabDevice.isTZT) {
    xfnReplace(fullUrl, options?.webviewConfig);
    return;
  }
  window.location.replace(fullUrl);
}

/**
 * 返回上一级路由
 *
 * 在小福牛中将调用app返回的方法
 * @param step 返回的层级 仅在浏览器中有效
 */
export function goback(step = -1) {
  if (crabDevice.isTZT) {
    action10002();
    return;
  }
  RouterConfig.history.go(step);
}

/**
 * 生成小福牛跳转地址
 *
 * @param path 跳转地址
 */
export function generateXFNUrl(url: string) {
  if (crabDevice.isTZT) {
    const { pathname } = window.location;
    // 获取hashRouter pathname 并于hash值拼接生成完整的跳转路径
    return `${pathname.replace(/\/$/, '')}#${url}`;
  }
  return url;
}

/**
 * 生成完成的外部页面地址， 不含请求参数
 *
 * @param url 外部页面地址
 */
export function generateExtraFullUrl(url: string) {
  if (/^http:/.test(url)) {
    return url;
  }
  return (process.env.REACT_APP_EXTRA_BASE_URL || '') + url;
}

/**
 * 路由replace跳转
 *
 * @param url 页面地址
 * @param query 请求参数
 * @param webviewConfig webview配置
 */
function xfnReplace(url: string, webviewConfig?: Action10061Options) {
  crabReplace(url, webviewConfig);
}

/**
 *
 * @param data 需要保存的数据
 */
async function saveExtraMapMesg(data?: ExtraMapMesg) {
  if (!data) {
    return;
  }
  // eslint-disable-next-line no-restricted-syntax
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const element = data[key];
      if (typeof element === 'object') {
        // eslint-disable-next-line no-param-reassign
        data[key] = JSON.stringify(element);
      }
    }
  }
  await saveMapMesgCross(data as Record<string, string>);
}
